import { apm } from "@elastic/apm-rum";
import { mm } from "@inovitas/infra3dsdk";
import {
  Autocomplete,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import LayerSettings from "shared/ui/toolbar/tools/layertreeAndEdit/layerContent/addLayerItems/layerSettings/LayerSettings";
import { monitor } from "shared/common/services/apm-logging";
import { defaultStore } from "shared/common/stores/defaultStore";
import { sidebarStore } from "shared/common/stores/sidebarStore";
import PopoverElement from "shared/common/utils/popover/PopoverElement";
import {
  getFeaturesCountFromLayer,
  IUnsavedLayerChanges,
} from "../../../../../common/utils/layers/layerutils";
import { IFeatureStyle } from "@inovitas/infra3dsdk/dist/types/mm/featuretree/interfaces/IFeatureStyle";

export default function EditLayerSettings({
  editLayerSettings,
}: {
  editLayerSettings: mm.Layer;
}) {
  const { t } = useTranslation();

  const viewer = defaultStore((state) => state.viewer);
  const darkTheme = defaultStore((state) => state.darkTheme);
  const changeEditLayerSettings = defaultStore(
    (state) => state.changeEditLayerSettings
  );

  const layerGroups = sidebarStore((state) => state.layerGroups);
  const changeLayerGroups = sidebarStore((state) => state.changeLayerGroups);

  const [canSave, setCanSave] = useState<boolean>(true);

  const [unsavedLayerChanges, setUnsavedLayerChanges] =
    useState<IUnsavedLayerChanges>({
      title: editLayerSettings.title,
      topic: editLayerSettings.topic,
      geometrytype: editLayerSettings.geometrytype,
      propertydef: {
        add: [],
        remove: [],
        rename: [],
        applied: editLayerSettings.propertydef ?? [],
      },
      style: JSON.parse(editLayerSettings.style.toJSON()) ?? {
        stylename: "",
        rules: [],
      },
    });

  const [editPropertyDefDataType, setEditPropertyDefDataType] =
    useState<boolean>(false);

  useEffect(() => {
    getFeaturesCountFromLayer(editLayerSettings).then((count) => {
      if (count === 0) {
        setEditPropertyDefDataType(true);
      } else {
        setEditPropertyDefDataType(false);
      }
    });
  }, [editLayerSettings]);

  //remove point object if they removed point from selection
  const removePointObject = (style: IFeatureStyle): IFeatureStyle => {
    style.rules.forEach((st, index: number) => {
      if (st.symbolizer === "point") {
        if (!("font-marker" in st.params)) {
          style.rules = style.rules.filter(
            (item, stIndex: number) => index !== stIndex
          );
        }
      }
    });

    return style;
  };

  const rulesWithFilters =
    editLayerSettings.style.rules
      .filter((rule) => rule.filter && rule.filter.trim() !== "")
      .filter(
        (rule, index, self) =>
          self.findIndex((r) => r.filter === rule.filter) === index
      ) ?? [];

  const editStyle = rulesWithFilters.length === 0;
  const editProperties = editLayerSettings.type === mm.LAYERTYPE.MEMORYLAYER;

  //change layer name
  const changeLayerName = (value: string) => {
    setUnsavedLayerChanges({ ...unsavedLayerChanges, title: value });
    if (value.length === 0) {
      setCanSave(false);
    } else {
      setCanSave(true);
    }
  };

  //change layer group
  const changeGroup = (value: string) => {
    setUnsavedLayerChanges({ ...unsavedLayerChanges, topic: value });
  };

  //update changes for localstorage layers
  const handleSaveChanges = () => {
    const allLayers: mm.Layer[] | undefined = viewer
      ?.getScene()
      .getLayerHandler().layers;
    if (allLayers === undefined) return;
    const layer = allLayers.find((item) => item.id === editLayerSettings.id);
    if (layer === undefined) return;

    const new_options: Record<string, unknown> = {};
    console.log(unsavedLayerChanges, editLayerSettings);
    if (unsavedLayerChanges.title !== editLayerSettings.title) {
      new_options["title"] = unsavedLayerChanges.title;
    }
    if (unsavedLayerChanges.topic !== editLayerSettings.topic) {
      new_options["topic"] = unsavedLayerChanges.topic;
    }
    if (
      JSON.stringify(unsavedLayerChanges.style) !==
      editLayerSettings.style.toJSON()
    ) {
      new_options["style"] = unsavedLayerChanges.style
        ? removePointObject(unsavedLayerChanges.style)
        : undefined;
    }
    if (
      unsavedLayerChanges.propertydef.add.length > 0 ||
      unsavedLayerChanges.propertydef.remove.length > 0 ||
      unsavedLayerChanges.propertydef.rename.length > 0
    ) {
      new_options["propertydef"] = unsavedLayerChanges.propertydef;
    }

    layer.setOptions(new_options);

    changeEditLayerSettings(undefined);
  };

  return (
    <PopoverElement
      maximizeTable={true}
      visible={true}
      handleClose={() => changeEditLayerSettings(undefined)}
      title={t("edit_layers.title")}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          paddingY: 1,
          gap: 3,
          height: "100%",
          color: darkTheme ? "white" : "darkblue.main",
        }}
      >
        <Box
          sx={{
            display: "flex",
            gap: 2,
            flexDirection: { xs: "column", md: "row" },
          }}
        >
          <TextField
            defaultValue={editLayerSettings.title}
            color="primary"
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
            size="small"
            label={t("create_layer.name")}
            variant="outlined"
            onChange={(event) => changeLayerName(event.target.value)}
          />
          <Autocomplete
            freeSolo
            disabled={false}
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
            size="small"
            options={layerGroups}
            noOptionsText={t("create_layer.group_helper")}
            value={editLayerSettings.topic ?? ""}
            onBlur={(event: React.FocusEvent<HTMLInputElement>) => {
              changeGroup(event.target.value);
              if (
                !layerGroups.includes(event.target.value) &&
                event.target.value !== ""
              ) {
                changeLayerGroups(layerGroups.concat(event.target.value));
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("create_layer.group")}
                variant="outlined"
              />
            )}
          />
          <FormControl
            size="small"
            sx={{
              width: { xs: "100%", md: "25%" },
            }}
          >
            <InputLabel>{t("create_layer.geometry_type")}</InputLabel>
            <Select
              disabled
              label="Geometry Type"
              defaultValue={editLayerSettings.geometrytype}
            >
              <MenuItem value={mm.GEOMETRYTYPE.POINT}>
                {t("create_layer.geometry_type_1")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTIPOINT}>
                {t("create_layer.geometry_type_4")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.LINESTRING}>
                {t("create_layer.geometry_type_2")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTILINESTRING}>
                {t("create_layer.geometry_type_5")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.POLYGON}>
                {t("create_layer.geometry_type_3")}
              </MenuItem>
              <MenuItem value={mm.GEOMETRYTYPE.MULTIPOLYGON}>
                {t("create_layer.geometry_type_6")}
              </MenuItem>
            </Select>
          </FormControl>
          <Button
            disabled={!canSave}
            sx={{
              borderColor: darkTheme ? "white" : "primary.main",
              color: darkTheme ? "white" : "primary.main",
            }}
            variant="outlined"
            onClick={() =>
              monitor(
                apm,
                "save-edit-layer | Layer Settings",
                handleSaveChanges
              )
            }
          >
            {t("manage_layers.button_save")}
          </Button>
        </Box>
        <LayerSettings
          unsavedLayerChanges={unsavedLayerChanges}
          setUnsavedLayerChanges={setUnsavedLayerChanges}
          editStyle={editStyle}
          editProperties={editProperties}
          editPropertyDefDataType={editPropertyDefDataType}
          canSaveCBF={(canSave: boolean) => {
            setCanSave(canSave);
          }}
        />
      </Box>
    </PopoverElement>
  );
}
